import { type Option } from "@tento-lona/sheets";
import { RowKey, RowLocalId } from "@tento-lona/sheets";
import type { Inset } from "@tento-core/inset";
import { SheetViewModel } from "@tento-lona";
import { LiveData } from "@tento-core/livedata";
import { Tracing } from "@tento-core/tracing";
import { Point } from "@tento-core/point";
import { ScrollPopover } from "@tento-ui/components/scroll/scroll-popover";
import { ColumnIndex } from "@tento-ui/components/sheet/column-index";
import { SheetLayoutPort } from "@tento-ui/components/sheet/sheet-layout";
import { SheetDelegate } from "./sheet-delegate.ts";
import { SheetBinder } from "./sheet-index-renderer.ts";
import { SheetCellTargets } from "./sheet-cell-targets.ts";
import { SheetElementLayout } from "./sheet-element-layout.ts";
import type { SheetElementCore } from "./sheet-element-core.ts";

/**
 * The sheet element's popover handle (F11): reveal and dismiss row-anchored
 * popovers through the layout's overlay surface. Moved out of
 * sheet-element.ts unchanged; the element hands in its sheet, overlays,
 * anchor resolution and layout constants, read when a method runs.
 */

/** What the handle reads from its sheet element (read when a method runs). */
export type PopoverHandleHost = {
  /** The bound sheet, if any. */
  readonly sheet: Option<SheetViewModel.Surface<SheetBinder>>;
  /** The layout's overlay surfaces. */
  readonly overlays: SheetLayoutPort["overlays"];
  /** The row (and its surface) a popover on `rowId` anchors to. */
  readonly resolveAnchor: (rowId: RowLocalId) => { rowId: RowLocalId; location: SheetDelegate.SurfaceLocation };
  /** `SheetElement.topContentPaddingPx`. */
  readonly topContentPaddingPx: number;
  /** `SheetElement.Layout.bodyMarginTop`. */
  readonly bodyMarginTop: (sheet: SheetViewModel.Surface<SheetBinder>) => number;
};

/** The handle the element exposes as `popoverHandle`. */
export function makePopoverHandle(host: PopoverHandleHost): SheetDelegate.PopoverHandle {
  return {
      surface: (position) => {
        return host.resolveAnchor(position.y).location;
      },
      reveal: (
        identifier: string,
        $content: HTMLElement,
        position: Point<ColumnIndex.Key.Resolved.WithSubindex, RowLocalId>,
        options: Option<ScrollPopover.Options>,
      ) => {
        const rid = position.y;
        const anchor = host.resolveAnchor(rid);

        const rowLayout =
          host.sheet?.layout.rowLayout.get(anchor.rowId) ??
          host.sheet?.layout.rowLayout.get(rid);
        if (!rowLayout) {
          Tracing.err("ui")("NO LAYOUT", position.y, host.sheet?.layout);
        }

        // Position exactly like the owning row's own popover would: its
        // region-relative `offsetPx` (+ the body's top padding), unified with the
        // `rowLayout` branch every other popover uses. The `headerHeight` added
        // downstream in scroll-popover completes the layer mapping per region.
        // Fallback (no layout at all — a layout-less body row) keeps the
        // `bodyMarginTop` shift so the popover doesn't float a header-height too
        // high.
        const yOffset = rowLayout
          ? rowLayout.offsetPx +
            (anchor.location === "body" ? host.topContentPaddingPx : 0)
          : anchor.location === "body" && host.sheet
          ? host.bodyMarginTop(host.sheet)
          : 0;

        host.overlays.popover.reveal(
          identifier,
          $content,
          {
            x: position.x,
            y: new LiveData(yOffset),
          },
          anchor.location,
          options ?? undefined,
        );
      },
      dismiss: (identifier: string) => {
        host.overlays.popover.dismiss(identifier);
      },
      currentId: () => {
        return host.overlays.popover.currentId() ?? null;
      },
  };
}

/**
 * The row a popover or tooltip on `rowId` anchors to, and its surface (moved
 * out of sheet-element.ts with the tooltip handle, r5 FS 2).
 */
export function resolvePopoverAnchor(
sheet: Option<SheetViewModel.Surface<SheetBinder>>,
rowId: RowLocalId,
): { rowId: RowLocalId; location: SheetDelegate.SurfaceLocation } {
  // Resolve which row to read region + layout from. Two virtual-row shapes
  // reach here and they need OPPOSITE handling:
  //   - Create-form / new-event popover: not independently rendered; read
  //     the owner row.
  //   - All-day projection row: independently rendered; keep its own region.
  const virtual = rowId.key.asVirtual();
  const rowLocation = sheet?.joinedTree.getRowLocation(rowId) ?? null;
  const rowIsRendered = rowLocation != null;
  const anchorRowId =
    virtual != null && !rowIsRendered && sheet
      ? sheet.ids.resolve(RowKey.user(virtual.parentHex))
      : rowId;
  const anchorLocation =
    sheet?.joinedTree.getRowLocation(anchorRowId) ?? "body";
  return {
    rowId: anchorRowId,
    location: anchorLocation,
  };
}

/**
 * The handle the element exposes as `tooltipHandle`: reveal and dismiss
 * row-anchored tooltips through the same host as the popover handle (moved out
 * of sheet-element.ts unchanged, r5 FS 2).
 */
export function makeTooltipHandle(host: PopoverHandleHost): SheetDelegate.TooltipHandle {
  return {
    showTooltip(
      identifier: string,
      $e: HTMLElement,
      position: Point<ColumnIndex.Key.Resolved.WithSubindex, RowLocalId>,
      align: "top" | "bottom",
      options: Option<{
        margin?: Inset;
        offset?: Point;
      }>,
    ) {
      const sheet = host.sheet;
      if (!sheet) return;
      const rid = position.y;
      // A logical row id may be a non-independently-rendered virtual row (e.g. a
      // weather alias-child projection), whose own layout entry is absent — its
      // cells paint under a rendered anchor row. Resolve to that anchor the same
      // way popovers do (resolvePopoverAnchor), so the tooltip reads the rendered
      // layout instead of silently bailing on a missing one.
      const anchor = host.resolveAnchor(rid);
      const location = anchor.location;
      const rowLayout =
        sheet.layout.rowLayout.get(anchor.rowId) ??
        sheet.layout.rowLayout.get(rid);
      if (!rowLayout) return;

      let offY =
        rowLayout.offsetPx +
        (location === "body" ? host.topContentPaddingPx : 0);
      if (align === "bottom") {
        offY += rowLayout.cellHeightWithChildrenPx;
      }
      offY += options?.offset?.y ?? 0;

      host.overlays.tooltip.reveal(
        identifier,
        $e,
        {
          x: position.x,
          y: new LiveData(offY),
        },
        location,
        {
          anchorHeight: rowLayout.cellHeightWithChildrenPx,
          margin: options?.margin,
        },
      );
    },
    hideTooltip(identifier: string) {
      host.overlays.tooltip.dismiss(identifier);
    },
  };
}

/**
 * The popover and tooltip handles' host over the element's core: its bound
 * sheet, the layout's overlays, the anchor resolution and the layout
 * constants, each read when a method runs.
 */
export function popoverHostOf(core: SheetElementCore): PopoverHandleHost {
  return {
    get sheet() {
      return core.sheet;
    },
    get overlays() {
      return core.layout.overlays;
    },
    resolveAnchor: (rowId) => resolvePopoverAnchor(core.sheet, rowId),
    topContentPaddingPx: SheetCellTargets.topContentPaddingPx,
    bodyMarginTop: (sheet) => SheetElementLayout.bodyMarginTop(sheet),
  };
}
